import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";

export interface SignalCardBodyData {
  headline: string;
  why_now: string;
  first_step: string;
  evidence_url?: string | null;
  evidence_quote?: string | null;
  draft_subject?: string | null;
  draft_body?: string | null;
}

/**
 * Innholdet i et signalkort: overskrift, hvorfor nå, første steg, bevis og
 * sammenleggbart mailutkast. Brukes av SignalCard i køen og av
 * prøvekjøringen (AgentDryRunPanel), så kortene ser like ut begge steder.
 */
export default function SignalCardBody({ signal }: { signal: SignalCardBodyData }) {
  const [draftOpen, setDraftOpen] = useState(false);
  const hasDraft = !!signal.draft_body?.trim();

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold leading-snug">{signal.headline}</h3>
      <section>
        <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Hvorfor nå</h4>
        <p className="mt-1 text-sm">{signal.why_now}</p>
      </section>
      <section>
        <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Forslag til første steg</h4>
        <p className="mt-1 text-sm">{signal.first_step}</p>
      </section>
      {(signal.evidence_url || signal.evidence_quote) && (
        <section>
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Bevis</h4>
          {signal.evidence_quote && (
            <blockquote className="mt-1 border-l-2 border-border pl-3 text-sm text-muted-foreground">
              {signal.evidence_quote}
            </blockquote>
          )}
          {signal.evidence_url && (
            <a
              href={signal.evidence_url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Åpne kilden <SrIcon name="external-link" className="h-3.5 w-3.5" />
            </a>
          )}
        </section>
      )}
      {hasDraft && (
        <Collapsible open={draftOpen} onOpenChange={setDraftOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" aria-label="Vis eller skjul mailutkast">
              {draftOpen ? "Skjul mailutkast" : "Se mailutkast"}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 rounded border border-border bg-muted/30 p-3 text-sm">
            {signal.draft_subject && <p className="font-medium">{signal.draft_subject}</p>}
            <p className="mt-1 whitespace-pre-wrap">{signal.draft_body}</p>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
